"use client";
import React, { useEffect, useRef } from "react";
import { startAdSenseUnit } from "@/lib/ads/adsenseRuntime";
import { observeAdSenseConsent } from "@/lib/ads/adsenseConsent";
import { claimDisplayProvider } from "@/lib/ads/externalDisplayOwnership";
import { useConsent } from "@/components/privacy/ConsentRuntime";
import { emitBrowserAdDiagnostic } from "@/lib/ads/deliveryDiagnostics";

export default function AdSenseRenderer({ decision, publicationId, placementIdentifier,
  onRenderState, onRetire, onFailure }) {
  const { canRequestAdvertising } = useConsent();
  const node = useRef(null);
  const current = useRef({ canRequestAdvertising, onRenderState, onRetire, onFailure });
  current.current = { canRequestAdvertising, onRenderState, onRetire, onFailure };
  // AdSense direct: the publication's own advertising consent decides (the decision API already
  // required it); no TCF CMP. An unfilled or failed unit falls back once to House/Direct.
  useEffect(() => {
    if (!decision?.liveDeliveryApproved || !decision.payload?.direct || !node.current) return;
    const report = code => emitBrowserAdDiagnostic(placementIdentifier, code);
    if (current.current.canRequestAdvertising?.() !== true) { report("CONSENT_REQUIRED"); current.current.onRetire?.("CONSENT_REQUIRED"); return; }
    if (!claimDisplayProvider(publicationId, placementIdentifier, "ADSENSE")) { report("EXTERNAL_PROVIDER_PINNED"); current.current.onRetire?.("EXTERNAL_PROVIDER_PINNED"); return; }
    let cleanup;
    try {
      cleanup = startAdSenseUnit({
        container: node.current, publicationId, placement: placementIdentifier,
        publisherId: decision.payload.publisherId, slotId: decision.payload.slotId,
        personalized: decision.personalizedAdvertisingAllowed === true,
        canRequest: () => current.current.canRequestAdvertising?.() === true && (!decision.expiresAt || Date.now() < decision.expiresAt),
        onState: code => {
          report(code);
          if (code === "ADSENSE_FILLED") current.current.onRenderState?.(true);
          else if (["CONSENT_REQUIRED", "CONSENT_WITHDRAWN"].includes(code)) { current.current.onRenderState?.(false); current.current.onRetire?.(code); }
          else if (!["ADSENSE_LOADING", "ADSENSE_REQUEST_INVOKED"].includes(code)) { current.current.onRenderState?.(false); current.current.onFailure?.(code); }
        }
      });
    } catch {
      report("ADSENSE_CONFIGURATION_INVALID");
      current.current.onFailure?.("ADSENSE_CONFIGURATION_INVALID");
    }
    return () => { cleanup?.(); current.current.onRenderState?.(false); };
  }, [decision, publicationId, placementIdentifier]);
  useEffect(() => {
    if (!decision?.liveDeliveryApproved || decision.payload?.direct || !node.current) return;
    let cleanup, unsubscribe, googleAllowed = false, started = false;
    const report = code => emitBrowserAdDiagnostic(placementIdentifier, code);
    const consentTimer = setTimeout(() => { if (!started) current.current.onRetire?.("ADSENSE_CONSENT_PENDING"); }, 10000);
    report("ADSENSE_CONSENT_PENDING");
    try {
      unsubscribe = observeAdSenseConsent(decision.payload?.cmpId, allowed => {
        googleAllowed = allowed;
        if (!allowed) { cleanup?.(); current.current.onRenderState?.(false); report("ADSENSE_CONSENT_PENDING"); if (started) current.current.onRetire?.("CONSENT_WITHDRAWN"); return; }
        if (started || current.current.canRequestAdvertising?.() !== true) return;
        if (!claimDisplayProvider(publicationId, placementIdentifier, "ADSENSE")) { report("EXTERNAL_PROVIDER_PINNED"); return; }
        started = true;
        clearTimeout(consentTimer);
        cleanup = startAdSenseUnit({
        container: node.current, publicationId, placement: placementIdentifier,
        publisherId: decision.payload?.publisherId, slotId: decision.payload?.slotId,
        canRequest: () => googleAllowed && current.current.canRequestAdvertising?.() === true && (!decision.expiresAt || Date.now() < decision.expiresAt),
        onState: code => {
          report(code);
          if (code === "ADSENSE_FILLED") current.current.onRenderState?.(true);
          else if (!["ADSENSE_LOADING", "ADSENSE_REQUEST_INVOKED"].includes(code)) { current.current.onRenderState?.(false); current.current.onRetire?.(code); }
        }
      });
      });
    } catch {
      clearTimeout(consentTimer);
      report("ADSENSE_CONFIGURATION_INVALID");
      current.current.onRenderState?.(false);
      current.current.onRetire?.("ADSENSE_CONFIGURATION_INVALID");
    }
    return () => { clearTimeout(consentTimer); unsubscribe?.(); cleanup?.(); current.current.onRenderState?.(false); };
  }, [decision, publicationId, placementIdentifier]);
  return <div ref={node} style={{ width: "100%", minWidth: 0 }} aria-label="Advertisement" />;
}
